/* eslint-disable @typescript-eslint/no-implicit-any-catch,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return */
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@modulz/design-system";
import { useCallback, useEffect, useState, useRef } from "preact/hooks";
import { h, JSX, FunctionComponent } from "preact";
import { MOCK_TABLE } from "./constants";

import {
  DatasetMap,
  PluginTabs,
  WidgetConfig,
  EditorMessage,
  ApplicationState,
  WidgetToRendererMessage,
} from "./graphpad.types";
import { DataDefinitionPanel } from "./panels/data-definition-panel";
import { HelpPanel } from "./panels/help-panel";
import { postMessageToFigjam } from "./panels/post-message-to-figjam";
import { SpecPanel } from "./panels/spec-panel";
import { SPEC_PRESETS } from "./panels/spec-presets";
import { handleRenderGraphMessage } from "./renderer-api";

// OK with returning "any" for the time being since the prismjs library is missing types.
// import { Container, TabsOption } from "@create-figma-plugin/ui";
// import { emit } from "@create-figma-plugin/utilities";
// import { h } from "preact";

// (A lil night GPT poetry)
// roses are red
// theme="dark"
// violets are blue
// sunflowers are yellow
// daisies are pink
// lilies are purple
// daffodils are orange
// irises are green
// tulips are purple
// carnations are pink
// dandelions are yellow

interface PluginTabsOption {
  value: PluginTabs;
  children: JSX.Element;
}

interface UiContainerProps {
  text: string;
  initialTheme: string;
  datasets: DatasetMap;
  widgetConfig: WidgetConfig;
  activeTab: PluginTabs;
}

const EditorApp: FunctionComponent<UiContainerProps> = (props) => {
  const [selectedTab, setSelectedTab] = useState<PluginTabs>(props.activeTab);

  // Need to save this state locally so that data can be updated without refreshing
  const [graphSpec, setGraphSpec] = useState<string>(props.text);
  const [localDatasets, setLocalDatasets] = useState<DatasetMap>(
    props.datasets
  );

  const themeRef = useRef<string>(props.initialTheme);
  // const [localTheme, setLocalTheme] = useState<string>(
  //   props.initialTheme
  // );

  // Update local state when parent changes
  useEffect(() => {
    setLocalDatasets(props.datasets);
  }, [props.datasets]);

  useEffect(() => {
    setGraphSpec(props.text);
  }, [props.text]);

  const onSpecChange = useCallback(
    (newSpec: string) => {
      try {
        // disable no-undef on next line
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const parsed = JSON.parse(newSpec);
        // console.log({ localTheme })
        // just emit string
        // only do parsing for error guard
        // console.log('parsed definition correctly', parsed);
        // emit("UPDATE_TEXT", newSpec);
        setGraphSpec(newSpec);
        postMessageToFigjam({
          type: "UPDATE_DATA_AND_SPEC_AND_THEME",
          data: {
            graphSpec: newSpec,
            datasets: localDatasets,
            theme: themeRef.current,
          },
        });
        // postMessageToFigjam()
      } catch (error) {
        console.warn("There was issue with your JSON", error);
      }
    },
    [localDatasets]
  );

  const onDatasetsChange = useCallback(
    (newDatasets: DatasetMap) => {
      setLocalDatasets(newDatasets);
      console.log({ newDatasets });
      postMessageToFigjam({
        type: "UPDATE_DATA_AND_SPEC_AND_THEME",
        data: {
          graphSpec,
          datasets: newDatasets,
          theme: themeRef.current,
        },
      });
    },
    [graphSpec]
  );

  const onThemeChange = useCallback(
    (newTheme: string) => {
      themeRef.current = newTheme;
      // setLocalTheme(newTheme);
      postMessageToFigjam({
        type: "UPDATE_DATA_AND_SPEC_AND_THEME",
        data: {
          graphSpec,
          datasets: localDatasets,
          theme: newTheme,
        },
      });
    },
    [graphSpec, localDatasets]
  );

  const options: PluginTabsOption[] = [
    {
      children: (
        <DataDefinitionPanel
          datasets={localDatasets}
          onDatasetsChange={onDatasetsChange}
        />
      ),
      value: "Choose Data",
    },
    {
      children: (
        <SpecPanel
          datasets={localDatasets}
          text={graphSpec}
          initialTheme={props.initialTheme}
          onSpecChange={onSpecChange}
          onThemeChange={onThemeChange}
        />
      ),
      value: "Define Graph",
    },
    {
      children: <HelpPanel widgetConfig={props.widgetConfig} />,
      value: "Help",
    },
  ];
  function handleTabsChange(value: string) {
    const newSelectedTab = value as PluginTabs;
    setSelectedTab(newSelectedTab);
    // emit("ACTIVE_TAB_CHANGE", newSelectedTab);
    postMessageToFigjam({
      type: "ACTIVE_TAB_CHANGE",
      data: {
        activeTab: newSelectedTab,
      },
    });
  }

  return (
    <div style={{ margin: 12 }}>
      <Tabs
        orientation="horizontal"
        onValueChange={handleTabsChange}
        value={selectedTab}
      >
        <TabsList>
          {options.map((option: PluginTabsOption) => (
            <TabsTrigger value={option.value} key={option.value}>
              {" "}
              {option.value}
            </TabsTrigger>
          ))}
        </TabsList>
        {options.map((option: PluginTabsOption) => (
          <TabsContent asChild value={option.value} key={option.value}>
            {option.children}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

const DEFAULT_APP_STATE = {
  text: JSON.stringify(SPEC_PRESETS.horizontalBar, null, 2),
  datasets: MOCK_TABLE,
  initialTheme: "no theme",
  activeTab: "Define Graph" as PluginTabs,
  widgetConfig: {
    editorDimensions: {
      height: 700,
      width: 500,
    },
  },
};

// const defaultProps = {
//   text: "",
//   datasets: {},
//   initialTheme: "dark",
//   widgetConfig: {
//     editorDimensions: {
//       width: 360,
//       height: 360,
//     },
//   },
//   activeTab: "Define Graph" as PluginTabs,
// };

export const EditorAppContainer: FunctionComponent<never> = () => {
  const [applicationState, setApplicationState] = useState<ApplicationState>();

  useEffect(() => {
    window.addEventListener(
      "message",
      (event: MessageEvent<any>) => {
        // Check if this is a message from the parent
        if (event.data.pluginMessage) {
          const pluginMessage: EditorMessage = event.data.pluginMessage;
          // console.log({ pluginMessage });
          if (pluginMessage.type === "GRAPHPAD_INITIALIZE") {
            // console.debug(
            //   "Instantiating app with host",
            //   pluginMessage.applicationState
            // );
            // console.log({ setApplicationState });
            setApplicationState(pluginMessage.applicationState);
          } else if (pluginMessage.type === "RENDER_GRAPH") {
            // Handle render request
            const renderData = (pluginMessage as WidgetToRendererMessage).data;

            // Use the handleRenderGraphMessage function to render the graph
            handleRenderGraphMessage(
              renderData,
              (svg) => {
                // On successful rendering, send the SVG back
                postMessageToFigjam({
                  type: "RENDER_COMPLETE",
                  data: { svg },
                });
              },
              (error, errorSvg) => {
                // On error, send the error SVG back
                console.error("Error rendering graph:", error);
                postMessageToFigjam({
                  type: "RENDER_COMPLETE",
                  data: { svg: errorSvg },
                });
              }
            );
          }
        }
      },
      false
    );
  }, [setApplicationState]);

  const allProps: ApplicationState = {
    ...DEFAULT_APP_STATE,
    ...applicationState,
  };
  // console.log({ applicationState  });

  return applicationState ? (
    <EditorApp {...allProps} />
  ) : (
    <EditorApp {...DEFAULT_APP_STATE} />
  );
};

// export default render(Plugin);
